/**
 *  Statistics interface
 *  @author Livescore <jsmith@example.com>
 *  @copyright 2022 livescore
 */

export enum STATSGROUP {
    INIT = 'init',
    POINTS = 'points',
    REBOUNDS = 'rebounds',
    ASSISTS_AND_TURNOVERS = 'assists_and_turnovers',
    ATTACK = 'attack',
    DEFENSE = 'defense',
    FACEOFFS = 'faceoffs',
    SHOOTING = 'shooting',
    CARDS = 'cards',
    BATTTING = 'batting',
    BOWLING = 'bowling',
    HITTING = 'hitting',
    PITCHING = 'pitching',
    FIELDING = 'fielding',
    EFFICIENCY = 'efficiency',
    FAST_BREAK = 'fast_break',
    SECOND_CHANCE = 'second_chance',
    SHOT_LOCATIONS = 'shot_locations',
    ACHIEVEMENTS = 'achievements',
    GENERAL = 'general',
    FOULS = 'fouls',
    EXTRA_POINTS = 'extra_points',
    AMERICAN_FOOTBALL_FIELD_GOALS = 'american_football_field_goals',
    FIRST_DOWNS = 'first_downs',
    FUMBLES = 'fumbles',
    INIT_RETURNS = 'init_returns',
    INTERCEPTIONS = 'interceptions',
    KICK_RETURNS = 'kick_returns',
    KICKOFFS = 'kickoffs',
    PASSING = 'passing',
    AMERICAN_FOOTBALL_PENALTIES = 'american_football_penalties',
    PUNT_RETURNS = 'punt_returns',
    PUNTS = 'punts',
    RECEIVING = 'receiving',
    RUSHING = 'rushing',
    TOUCHDOWNS = 'touchdowns',
    TOTAL = 'total',
    POWERPLAY = 'powerplay',
    SHORTHANDED = 'shorthanded',
    EVENSTRENGTH = 'evenstrength',
    PENALTY = 'penalty',
    AVERAGE = 'average',
}

export type OmitStatsKeys = 'tournamentName' | 'tournamentYear' | 'opta_competition';


export const STATSGROUPSDATA: Record<string, any> = {
    [STATSGROUP.INIT]: {
        matches_played: null,
        games_played: null,
        matches_won: null,
        matches_lost: null,
        minutes_played: null,
        overtime_losses: null,
        average_ball_possession: null,
        points: null,
        goals: null,
        goals_allowed: null,
        goals_scored: null,
        goals_scored_first_half: null,
        goals_scored_second_half: null,
        goals_conceded: null,
        goals_conceded_first_half: null,
        goals_conceded_second_half: null,
        goals_by_foot: null,
        goals_by_head: null,
        goals_by_penalty: null,
        own_goals: null,
        field_goals: null,
        assists: null,
        first_assists: null,
        second_assists: null,
        seven_m_goals: null,

        clean_sheets: null,

        // NFL
        stats_games_played: null,
    },

    [STATSGROUP.GENERAL]: {
        plus_minus: null,
        free_kicks: null,
        offsides: null,
        corner_kicks: null,
        penalties: null,
        number_of_penalties: null,
        penalty_minutes: null,
        penalties_missed: null,
        technical_fouls: null,
        shutouts: null,
        steals: null,
        interceptions: null,
        suspensions: null,
        passes_total: null,
        passes_successful: null,
        passes_unsuccessful: null,
        long_passes_total: null,
        long_passes_successful: null,
        long_passes_unsuccessful: null,
        crosses_total: null,
        crosses_successful: null,
        crosses_unsuccessful: null,
        substituted_in: null,
        substituted_out: null,
        dribbles_completed: null,
        loss_of_possession: null,

        // NBA
        total_games_played: null,
        total_games_started: null,
        total_minutes: null,
        average_minutes: null,
        total_bench_points: null,
        total_defensive_assists: null,
        total_defensive_rating: null,
        total_net_rating: null,
        total_offensive_rating: null,
        total_opponent_possessions: null,
        total_points_against: null,
        total_possessions: null,
        total_team_defensive_rebounds: null,
        total_team_offensive_rebounds: null,
        total_team_rebounds: null,
        total_team_turnovers: null,
        total_total_rebounds: null,
        total_total_turnovers: null,
        average_bench_points: null,

        // NHL
        // total_games_played: null,
        total_goals: null,
        total_assists: null,
        total_penalties: null,
        total_penalty_minutes: null,
        total_team_penalties: null,
        total_team_penalty_minutes: null,
        total_shots: null,
        total_blocked_att: null,
        total_missed_shots: null,
        total_hits: null,
        total_giveaways: null,
        total_takeaways: null,
        total_blocked_shots: null,
        total_powerplays: null,
        total_shooting_pct: null,
        total_points: null,
        total_emptynet_goals: null,
        total_overtime_assists: null,
        total_overtime_goals: null,
        total_overtime_shots: null,
        total_penalties_major: null,
        total_penalties_match: null,
        total_penalties_minor: null,
        total_penalties_misconduct: null,
        total_plus_minus: null,
        total_tying_goals: null,
    },

    // NBA
    [STATSGROUP.POINTS]: {
        total_points: null,
        total_points_in_paint: null,
        total_points_in_paint_att: null,
        total_points_in_paint_made: null,
        total_points_in_paint_pct: null,
        total_points_off_turnovers: null,
        total_second_chance_pts: null,
        total_fast_break_pts: null,
        average_points: null,
        average_points_in_paint: null,
        average_points_in_paint_att: null,
        average_points_in_paint_made: null,
        average_points_off_turnovers: null,
        average_second_chance_pts: null,
        average_fast_break_pts: null,
    },

    // NBA
    [STATSGROUP.REBOUNDS]: {
        total_rebounds: null,
        total_defensive_rebounds: null,
        total_offensive_rebounds: null,
        average_rebounds: null,
        average_def_rebounds: null,
        average_off_rebounds: null,
    },

    // NBA
    [STATSGROUP.ASSISTS_AND_TURNOVERS]: {
        total_assists: null,
        total_turnovers: null,
        total_assists_turnover_ratio: null,
        total_usage_pct: null,
        average_assists: null,
        average_turnovers: null,
    },

    [STATSGROUP.BATTTING]: {
        batting_highest_score_rank: null,
        batting_highest_score_total: null,
        batting_top_average_average: null,
        batting_top_average_rank: null,
        batting_top_fours_rank: null,
        batting_top_fours_total: null,
        batting_top_runs_rank: null,
        batting_top_runs_total: null,
        batting_top_sixes_rank: null,
        batting_top_sixes_total: null,
        batting_top_strike_rate_rank: null,
        batting_top_strike_rate_rate: null,
        batting_top_fifties_total: null,
        batting_top_fifties_rank: null,
    },
    [STATSGROUP.BOWLING]: {
        bowling_top_bowling_average_average: null,
        bowling_top_bowling_average_rank: null,
        bowling_top_dot_balls_rank: null,
        bowling_top_dot_balls_total: null,
        bowling_top_economy_rank: null,
        bowling_top_economy_rate: null,
        bowling_top_maidens_rank: null,
        bowling_top_maidens_total: null,
        bowling_top_wickets_rank: null,
        bowling_top_wickets_total: null,


    },

    [STATSGROUP.HITTING]: {
        // MLB
// hitting_overall
        hitting_overall_ab: null,
        hitting_overall_ab_risp: null,
        hitting_overall_abhr: null,
        hitting_overall_abk: null,
        hitting_overall_ap: null,
        hitting_overall_avg: null,
        hitting_overall_babip: null,
        hitting_overall_bbk: null,
        hitting_overall_bbpa: null,
        hitting_overall_bip: null,
        hitting_overall_flyball: null,
        hitting_overall_gofo: null,
        hitting_overall_groundball: null,
        hitting_overall_hit_risp: null,
        hitting_overall_iso: null,
        hitting_overall_linedrive: null,
        hitting_overall_lob: null,
        hitting_overall_lob_risp_2out: null,
        hitting_overall_obp: null,
        hitting_overall_ops: null,
        hitting_overall_popup: null,
        hitting_overall_rbi: null,
        hitting_overall_rbi_2out: null,
        hitting_overall_runs_total: null,
        hitting_overall_seca: null,
        hitting_overall_slg: null,
        hitting_overall_team_lob: null,
        hitting_overall_xbh: null,

        // hitting_overall_onbase
        hitting_overall_onbase_bb: null,
        hitting_overall_onbase_ci: null,
        hitting_overall_onbase_cycle: null,
        hitting_overall_onbase_d: null,
        hitting_overall_onbase_fc: null,
        hitting_overall_onbase_h: null,
        hitting_overall_onbase_hbp: null,
        hitting_overall_onbase_hr: null,
        hitting_overall_onbase_ibb: null,
        hitting_overall_onbase_roe: null,
        hitting_overall_onbase_rov: null,
        hitting_overall_onbase_s: null,
        hitting_overall_onbase_t: null,
        hitting_overall_onbase_tb: null,

        // hitting_overall_outcome
        hitting_overall_outcome_ball: null,
        hitting_overall_outcome_dirtball: null,
        hitting_overall_outcome_foul: null,
        hitting_overall_outcome_iball: null,
        hitting_overall_outcome_klook: null,
        hitting_overall_outcome_kswing: null,
        hitting_overall_outcome_ktotal: null,

        // hitting_overall_outs
        hitting_overall_outs_fidp: null,
        hitting_overall_outs_fo: null,
        hitting_overall_outs_gidp: null,
        hitting_overall_outs_go: null,
        hitting_overall_outs_klook: null,
        hitting_overall_outs_kswing: null,
        hitting_overall_outs_ktotal: null,
        hitting_overall_outs_lidp: null,
        hitting_overall_outs_lo: null,
        hitting_overall_outs_po: null,
        hitting_overall_outs_sacfly: null,
        hitting_overall_outs_sachit: null,

        // hitting_overall_pitches
        hitting_overall_pitch_count: null,
        hitting_overall_pitches_btotal: null,
        hitting_overall_pitches_count: null,
        hitting_overall_pitches_ktotal: null,

        // hitting_overall_steal
        hitting_overall_steal_caught: null,
        hitting_overall_steal_pct: null,
        hitting_overall_steal_pickoff: null,
        hitting_overall_steal_stolen: null,
        // ^ MLB ^
    },

    [STATSGROUP.PITCHING]: {
        // MLB
// pitching_bullpen
        pitching_bullpen_babip: null,
        pitching_bullpen_bf: null,
        pitching_bullpen_bf_ip: null,
        pitching_bullpen_bk: null,
        pitching_bullpen_era: null,
        pitching_bullpen_gbfb: null,
        pitching_bullpen_gofo: null,
        pitching_bullpen_ip_1: null,
        pitching_bullpen_ip_2: null,
        pitching_bullpen_k9: null,
        pitching_bullpen_kbb: null,
        pitching_bullpen_lob: null,
        pitching_bullpen_no_hitter: null,
        pitching_bullpen_oab: null,
        pitching_bullpen_oba: null,
        pitching_bullpen_obp: null,
        pitching_bullpen_perfect_game: null,
        pitching_bullpen_pitch_count: null,
        pitching_bullpen_slg: null,
        pitching_bullpen_whip: null,
        pitching_bullpen_wp: null,

        // pitching_bullpen_games
        pitching_bullpen_games_blown_save: null,
        pitching_bullpen_games_complete: null,
        pitching_bullpen_games_hold: null,
        pitching_bullpen_games_loss: null,
        pitching_bullpen_games_qstart: null,
        pitching_bullpen_games_save: null,
        pitching_bullpen_games_shutout: null,
        pitching_bullpen_games_svo: null,
        pitching_bullpen_games_team_shutout: null,
        pitching_bullpen_games_win: null,

        // pitching_bullpen_in_play
        pitching_bullpen_in_play_flyball: null,
        pitching_bullpen_in_play_groundball: null,
        pitching_bullpen_in_play_linedrive: null,
        pitching_bullpen_in_play_popup: null,

        // pitching_bullpen_onbase
        pitching_bullpen_onbase_bb: null,
        pitching_bullpen_onbase_ci: null,
        pitching_bullpen_onbase_d: null,
        pitching_bullpen_onbase_fc: null,
        pitching_bullpen_onbase_h: null,
        pitching_bullpen_onbase_h9: null,
        pitching_bullpen_onbase_hbp: null,
        pitching_bullpen_onbase_hr: null,
        pitching_bullpen_onbase_hr9: null,
        pitching_bullpen_onbase_ibb: null,
        pitching_bullpen_onbase_roe: null,
        pitching_bullpen_onbase_rov: null,
        pitching_bullpen_onbase_s: null,
        pitching_bullpen_onbase_t: null,
        pitching_bullpen_onbase_tb: null,

        // pitching_bullpen_outcome
        pitching_bullpen_outcome_ball: null,
        pitching_bullpen_outcome_dirtball: null,
        pitching_bullpen_outcome_foul: null,
        pitching_bullpen_outcome_iball: null,
        pitching_bullpen_outcome_klook: null,
        pitching_bullpen_outcome_kswing: null,
        pitching_bullpen_outcome_ktotal: null,

        // pitching_bullpen_outs
        pitching_bullpen_outs_fidp: null,
        pitching_bullpen_outs_fo: null,
        pitching_bullpen_outs_gidp: null,
        pitching_bullpen_outs_go: null,
        pitching_bullpen_outs_klook: null,
        pitching_bullpen_outs_kswing: null,
        pitching_bullpen_outs_ktotal: null,
        pitching_bullpen_outs_lidp: null,
        pitching_bullpen_outs_lo: null,
        pitching_bullpen_outs_po: null,
        pitching_bullpen_outs_sacfly: null,
        pitching_bullpen_outs_sachit: null,

        // pitching_bullpen_pitches
        pitching_bullpen_pitches_btotal: null,
        pitching_bullpen_pitches_count: null,
        pitching_bullpen_pitches_ktotal: null,
        pitching_bullpen_pitches_per_bf: null,
        pitching_bullpen_pitches_per_ip: null,

        // pitching_bullpen_runs
        pitching_bullpen_runs_bqr: null,
        pitching_bullpen_runs_bqra: null,
        pitching_bullpen_runs_earned: null,
        pitching_bullpen_runs_ir: null,
        pitching_bullpen_runs_ira: null,
        pitching_bullpen_runs_total: null,
        pitching_bullpen_runs_unearned: null,

        // pitching_bullpen_steal
        pitching_bullpen_steal_caught: null,
        pitching_bullpen_steal_pickoff: null,
        pitching_bullpen_steal_stolen: null,

        // pitching_overall
        pitching_overall_babip: null,
        pitching_overall_bf: null,
        pitching_overall_bf_ip: null,
        pitching_overall_bf_start: null,
        pitching_overall_bk: null,
        pitching_overall_era: null,
        pitching_overall_gbfb: null,
        pitching_overall_gofo: null,
        pitching_overall_ip_1: null,
        pitching_overall_ip_2: null,
        pitching_overall_k9: null,
        pitching_overall_kbb: null,
        pitching_overall_lob: null,
        pitching_overall_no_hitter: null,
        pitching_overall_oab: null,
        pitching_overall_oba: null,
        pitching_overall_obp: null,
        pitching_overall_perfect_game: null,
        pitching_overall_pitch_count: null,
        pitching_overall_slg: null,
        pitching_overall_whip: null,
        pitching_overall_wp: null,

        // pitching_overall_games
        pitching_overall_games_blown_save: null,
        pitching_overall_games_complete: null,
        pitching_overall_games_hold: null,
        pitching_overall_games_loss: null,
        pitching_overall_games_qstart: null,
        pitching_overall_games_save: null,
        pitching_overall_games_shutout: null,
        pitching_overall_games_svo: null,
        pitching_overall_games_team_shutout: null,
        pitching_overall_games_win: null,

        // pitching_overall_in_play
        pitching_overall_in_play_flyball: null,
        pitching_overall_in_play_groundball: null,
        pitching_overall_in_play_linedrive: null,
        pitching_overall_in_play_popup: null,

        // pitching_overall_onbase
        pitching_overall_onbase_bb: null,
        pitching_overall_onbase_ci: null,
        pitching_overall_onbase_d: null,
        pitching_overall_onbase_fc: null,
        pitching_overall_onbase_h: null,
        pitching_overall_onbase_h9: null,
        pitching_overall_onbase_hbp: null,
        pitching_overall_onbase_hr: null,
        pitching_overall_onbase_hr9: null,
        pitching_overall_onbase_ibb: null,
        pitching_overall_onbase_roe: null,
        pitching_overall_onbase_rov: null,
        pitching_overall_onbase_s: null,
        pitching_overall_onbase_t: null,
        pitching_overall_onbase_tb: null,

        // pitching_overall_outcome
        pitching_overall_outcome_ball: null,
        pitching_overall_outcome_dirtball: null,
        pitching_overall_outcome_foul: null,
        pitching_overall_outcome_iball: null,
        pitching_overall_outcome_klook: null,
        pitching_overall_outcome_kswing: null,
        pitching_overall_outcome_ktotal: null,

        // pitching_overall_outs
        pitching_overall_outs_fidp: null,
        pitching_overall_outs_fo: null,
        pitching_overall_outs_gidp: null,
        pitching_overall_outs_go: null,
        pitching_overall_outs_klook: null,
        pitching_overall_outs_kswing: null,
        pitching_overall_outs_ktotal: null,
        pitching_overall_outs_lidp: null,
        pitching_overall_outs_lo: null,
        pitching_overall_outs_po: null,
        pitching_overall_outs_sacfly: null,
        pitching_overall_outs_sachit: null,

        // pitching_overall_pitches
        pitching_overall_pitches_btotal: null,
        pitching_overall_pitches_count: null,
        pitching_overall_pitches_ktotal: null,
        pitching_overall_pitches_per_bf: null,
        pitching_overall_pitches_per_ip: null,
        pitching_overall_pitches_per_start: null,

        // pitching_overall_runs
        pitching_overall_runs_bqr: null,
        pitching_overall_runs_bqra: null,
        pitching_overall_runs_earned: null,
        pitching_overall_runs_ir: null,
        pitching_overall_runs_ira: null,
        pitching_overall_runs_total: null,
        pitching_overall_runs_unearned: null,

        // pitching_overall_steal
        pitching_overall_steal_caught: null,
        pitching_overall_steal_pickoff: null,
        pitching_overall_steal_stolen: null,

        // pitching_starters
        pitching_starters_babip: null,
        pitching_starters_bf: null,
        pitching_starters_bf_ip: null,
        pitching_starters_bf_start: null,
        pitching_starters_bk: null,
        pitching_starters_era: null,
        pitching_starters_gbfb: null,
        pitching_starters_gofo: null,
        pitching_starters_ip_1: null,
        pitching_starters_ip_2: null,
        pitching_starters_k9: null,
        pitching_starters_kbb: null,
        pitching_starters_lob: null,
        pitching_starters_no_hitter: null,
        pitching_starters_oab: null,
        pitching_starters_oba: null,
        pitching_starters_obp: null,
        pitching_starters_perfect_game: null,
        pitching_starters_pitch_count: null,
        pitching_starters_slg: null,
        pitching_starters_whip: null,
        pitching_starters_wp: null,

        // pitching_starters_games
        pitching_starters_games_blown_save: null,
        pitching_starters_games_complete: null,
        pitching_starters_games_hold: null,
        pitching_starters_games_loss: null,
        pitching_starters_games_qstart: null,
        pitching_starters_games_save: null,
        pitching_starters_games_shutout: null,
        pitching_starters_games_svo: null,
        pitching_starters_games_team_shutout: null,
        pitching_starters_games_win: null,

        // pitching_starters_in_play
        pitching_starters_in_play_flyball: null,
        pitching_starters_in_play_groundball: null,
        pitching_starters_in_play_linedrive: null,
        pitching_starters_in_play_popup: null,

        // pitching_starters_onbase
        pitching_starters_onbase_bb: null,
        pitching_starters_onbase_ci: null,
        pitching_starters_onbase_d: null,
        pitching_starters_onbase_fc: null,
        pitching_starters_onbase_h: null,
        pitching_starters_onbase_h9: null,
        pitching_starters_onbase_hbp: null,
        pitching_starters_onbase_hr: null,
        pitching_starters_onbase_hr9: null,
        pitching_starters_onbase_ibb: null,
        pitching_starters_onbase_roe: null,
        pitching_starters_onbase_rov: null,
        pitching_starters_onbase_s: null,
        pitching_starters_onbase_t: null,
        pitching_starters_onbase_tb: null,

        // pitching_starters_outcome
        pitching_starters_outcome_ball: null,
        pitching_starters_outcome_dirtball: null,
        pitching_starters_outcome_foul: null,
        pitching_starters_outcome_iball: null,
        pitching_starters_outcome_klook: null,
        pitching_starters_outcome_kswing: null,
        pitching_starters_outcome_ktotal: null,

        // pitching_starters_outs
        pitching_starters_outs_fidp: null,
        pitching_starters_outs_fo: null,
        pitching_starters_outs_gidp: null,
        pitching_starters_outs_go: null,
        pitching_starters_outs_klook: null,
        pitching_starters_outs_kswing: null,
        pitching_starters_outs_ktotal: null,
        pitching_starters_outs_lidp: null,
        pitching_starters_outs_lo: null,
        pitching_starters_outs_po: null,
        pitching_starters_outs_sacfly: null,
        pitching_starters_outs_sachit: null,

        // pitching_starters_pitches
        pitching_starters_pitches_btotal: null,
        pitching_starters_pitches_count: null,
        pitching_starters_pitches_ktotal: null,
        pitching_starters_pitches_per_bf: null,
        pitching_starters_pitches_per_ip: null,
        pitching_starters_pitches_per_start: null,

        // pitching_starters_runs
        pitching_starters_runs_bqr: null,
        pitching_starters_runs_bqra: null,
        pitching_starters_runs_earned: null,
        pitching_starters_runs_ir: null,
        pitching_starters_runs_ira: null,
        pitching_starters_runs_total: null,
        pitching_starters_runs_unearned: null,

        // pitching_starters_steal
        pitching_starters_steal_caught: null,
        pitching_starters_steal_pickoff: null,
        pitching_starters_steal_stolen: null,
        // ^ MLB ^
    },

    [STATSGROUP.FIELDING]: {
        fielding_top_catches_rank: null,
        fielding_top_catches_total: null,

        // MLB
        // fielding_overall
        fielding_overall_a: null,
        fielding_overall_assists_outfield: null,
        fielding_overall_assists_total: null,
        fielding_overall_c_wp: null,
        fielding_overall_dp: null,
        fielding_overall_error: null,
        fielding_overall_errors_fielding: null,
        fielding_overall_errors_interference: null,
        fielding_overall_errors_throwing: null,
        fielding_overall_errors_total: null,
        fielding_overall_fpct: null,
        fielding_overall_pb: null,
        fielding_overall_po: null,
        fielding_overall_steal_caught: null,
        fielding_overall_steal_pct: null,
        fielding_overall_steal_pickoff: null,
        fielding_overall_steal_stolen: null,
        fielding_overall_tc: null,
        fielding_overall_tp: null,
        // ^ MLB ^
    },
    [STATSGROUP.ATTACK]: {
        shots_total: null,
        shots_on_target: null,
        shots_off_target: null,
        shots_on_post: null,
        shots_on_bar: null,
        shots_faced: null,
        shots: null,
        shooting_percentage: null,
        chances_created: null,
    },
    [STATSGROUP.DEFENSE]: {
        shots_blocked: null,
        saves: null,
        save_percentage: null,
        blocks: null,
        clearances: null,
        tackles_total: null,
        tackles_successful: null,
        tackles_unsuccessful: null,

        // NFL
        defense_assists: null,
        defense_batted_passes: null,
        defense_blitzes: null,
        defense_combined: null,
        defense_def_comps: null,
        defense_def_targets: null,
        defense_forced_fumbles: null,
        defense_fourth_down_stops: null,
        defense_fumble_recoveries: null,
        defense_hurries: null,
        defense_interceptions: null,
        defense_knockdowns: null,
        defense_misc_assists: null,
        defense_misc_forced_fumbles: null,
        defense_misc_fumble_recoveries: null,
        defense_misc_tackles: null,
        defense_missed_tackles: null,
        defense_passes_defended: null,
        defense_qb_hits: null,
        defense_sack_yards: null,
        defense_sacks: null,
        defense_safeties: null,
        defense_sp_assists: null,
        defense_sp_blocks: null,
        defense_sp_forced_fumbles: null,
        defense_sp_fumble_recoveries: null,
        defense_sp_opp_fumble_recoveries: null,
        defense_sp_own_fumble_recoveries: null,
        defense_sp_tackles: null,
        defense_tackles: null,
        defense_three_and_outs_forced: null,
        defense_tloss: null,
        defense_tloss_yards: null,

        // NBA
        total_blocks: null,
        total_blocked_att: null,
        total_steals: null,
        average_blocks: null,
        average_blocked_att: null,
        average_steals: null,
    },

    // NHL
    [STATSGROUP.FACEOFFS]: {
        total_faceoffs_won: null,
        total_faceoffs_lost: null,
        total_faceoff_win_pct: null,
        total_faceoffs: null,
        shorthanded_faceoffs_won: null,
        shorthanded_faceoffs_lost: null,
        shorthanded_faceoff_win_pct: null,
        shorthanded_faceoffs: null,
        evenstrength_faceoffs_won: null,
        evenstrength_faceoffs_lost: null,
        evenstrength_faceoff_win_pct: null,
        evenstrength_faceoffs: null,
        powerplay_faceoffs_won: null,
        powerplay_faceoffs_lost: null,
        powerplay_faceoff_win_pct: null,
        powerplay_faceoffs: null,
    },

    // NBA
    [STATSGROUP.SHOOTING]: {
        total_field_goals_att: null,
        total_field_goals_made: null,
        total_field_goals_pct: null,
        total_two_points_att: null,
        total_two_points_made: null,
        total_two_points_pct: null,
        total_three_points_att: null,
        total_three_points_made: null,
        total_three_points_pct: null,
        total_free_throws_att: null,
        total_free_throws_made: null,
        total_free_throws_pct: null,
        total_true_shooting_att: null,
        total_true_shooting_pct: null,
        total_effective_fg_pct: null,
        average_field_goals_made: null,
        average_field_goals_att: null,
        average_two_points_att: null,
        average_two_points_made: null,
        average_three_points_att: null,
        average_three_points_made: null,
        average_free_throws_att: null,
        average_free_throws_made: null,
        average_true_shooting_att: null,
    },

    [STATSGROUP.CARDS]: {
        cards_given: null,
        red_cards: null,
        yellow_red_cards: null,
        yellow_cards: null,
    },

    [STATSGROUP.EFFICIENCY]: {
        // NFL
        efficiency_fourthdown_attempts: null,
        efficiency_fourthdown_pct: null,
        efficiency_fourthdown_successes: null,
        efficiency_goaltogo_attempts: null,
        efficiency_goaltogo_pct: null,
        efficiency_goaltogo_successes: null,
        efficiency_redzone_attempts: null,
        efficiency_redzone_pct: null,
        efficiency_redzone_successes: null,
        efficiency_thirddown_attempts: null,
        efficiency_thirddown_pct: null,
        efficiency_thirddown_successes: null,

        // NBA
        total_efficiency: null,
        total_plus: null,
        total_minus: null,
        average_efficiency: null,
    },

    // NBA
    [STATSGROUP.FAST_BREAK]: {
        total_fast_break_att: null,
        total_fast_break_made: null,
        total_fast_break_pct: null,
        average_fast_break_att: null,
        average_fast_break_made: null,
    },

    // NBA
    [STATSGROUP.SECOND_CHANCE]: {
        total_second_chance_att: null,
        total_second_chance_made: null,
        total_second_chance_pct: null,
        average_second_chance_att: null,
        average_second_chance_made: null,
    },

    // NBA
    [STATSGROUP.SHOT_LOCATIONS]: {
        total_field_goals_at_rim_att: null,
        total_field_goals_at_rim_made: null,
        total_field_goals_at_rim_pct: null,
        total_field_goals_at_midrange_att: null,
        total_field_goals_at_midrange_made: null,
        total_field_goals_at_midrange_pct: null,
    },

    // NBA
    [STATSGROUP.ACHIEVEMENTS]: {
        total_double_doubles: null,
        total_triple_doubles: null,
    },

    [STATSGROUP.FOULS]: {
        total_flagrant_fouls: null,
        total_offensive_fouls: null,
        total_personal_fouls: null,
        total_foulouts: null,
        total_fouls_drawn: null,
        total_tech_fouls: null,
        total_coach_tech_fouls: null,
        total_coach_ejections: null,
        total_ejections: null,


        total_team_fouls: null,


        total_team_tech_fouls: null,

        total_technical_other: null,
        total_total_fouls: null,


        total_tech_fouls_non_unsportsmanlike: null,
        average_flagrant_fouls: null,
        average_offensive_fouls: null,
        average_personal_fouls: null,
        average_fouls_drawn: null,


    },

    // NFL
    [STATSGROUP.EXTRA_POINTS]: {
        extra_points_conversions_defense_attempts: null,
        extra_points_conversions_defense_successes: null,
        extra_points_conversions_pass_attempts: null,
        extra_points_conversions_pass_successes: null,
        extra_points_conversions_rush_attempts: null,
        extra_points_conversions_rush_successes: null,
        extra_points_conversions_turnover_successes: null,
        extra_points_kicks_attempts: null,
        extra_points_kicks_blocked: null,
        extra_points_kicks_made: null,
        extra_points_kicks_pct: null,
    },
    // NFL
    [STATSGROUP.AMERICAN_FOOTBALL_FIELD_GOALS]: {
        field_goals_attempts: null,
        field_goals_attempts_19: null,
        field_goals_attempts_29: null,
        field_goals_attempts_39: null,
        field_goals_attempts_49: null,
        field_goals_attempts_50: null,
        field_goals_avg_yards: null,
        field_goals_blocked: null,
        field_goals_longest: null,
        field_goals_made: null,
        field_goals_made_19: null,
        field_goals_made_29: null,
        field_goals_made_39: null,
        field_goals_made_49: null,
        field_goals_made_50: null,
        field_goals_missed: null,
        field_goals_pct: null,
        field_goals_yards: null,
    },
    // NFL
    [STATSGROUP.FIRST_DOWNS]: {
        first_downs_pass: null,
        first_downs_penalty: null,
        first_downs_rush: null,
        first_downs_total: null,
    },
    // NFL
    [STATSGROUP.FUMBLES]: {
        fumbles_ez_rec_tds: null,
        fumbles_forced_fumbles: null,
        fumbles_fumbles: null,
        fumbles_lost_fumbles: null,
        fumbles_opp_rec: null,
        fumbles_opp_rec_tds: null,
        fumbles_opp_rec_yards: null,
        fumbles_out_of_bounds: null,
        fumbles_own_rec: null,
        fumbles_own_rec_tds: null,
        fumbles_own_rec_yards: null,
    },
    // NFL
    [STATSGROUP.INIT_RETURNS]: {
        int_returns_avg_yards: null,
        int_returns_longest: null,
        int_returns_longest_touchdown: null,
        int_returns_returns: null,
        int_returns_touchdowns: null,
        int_returns_yards: null,
    },
    // NFL
    [STATSGROUP.INTERCEPTIONS]: {
        interceptions_interceptions: null,
        interceptions_return_yards: null,
        interceptions_returned: null,
    },
    // NFL
    [STATSGROUP.KICK_RETURNS]: {
        kick_returns_avg_yards: null,
        kick_returns_faircatches: null,
        kick_returns_longest: null,
        kick_returns_longest_touchdown: null,
        kick_returns_returns: null,
        kick_returns_touchdowns: null,
        kick_returns_yards: null,
    },
    // NFL
    [STATSGROUP.KICKOFFS]: {
        kickoffs_endzone: null,
        kickoffs_inside_20: null,
        kickoffs_kickoffs: null,
        kickoffs_onside_attempts: null,
        kickoffs_onside_successes: null,
        kickoffs_out_of_bounds: null,
        kickoffs_return_yards: null,
        kickoffs_returned: null,
        kickoffs_squib_kicks: null,
        kickoffs_touchbacks: null,
        kickoffs_yards: null,
    },
    // NFL
    [STATSGROUP.PASSING]: {
        passing_air_yards: null,
        passing_attempts: null,
        passing_avg_yards: null,
        passing_batted_passes: null,
        passing_blitzes: null,
        passing_cmp_pct: null,
        passing_completions: null,
        passing_defended_passes: null,
        passing_dropped_passes: null,
        passing_gross_yards: null,
        passing_hurries: null,
        passing_int_touchdowns: null,
        passing_interceptions: null,
        passing_knockdowns: null,
        passing_longest: null,
        passing_longest_touchdown: null,
        passing_net_yards: null,
        passing_on_target_throws: null,
        passing_pocket_time: null,
        passing_poor_throws: null,
        passing_rating: null,
        passing_redzone_attempts: null,
        passing_sack_yards: null,
        passing_sacks: null,
        passing_spikes: null,
        passing_throw_aways: null,
        passing_touchdowns: null,
        passing_yards: null,
    },
    // NFL
    [STATSGROUP.AMERICAN_FOOTBALL_PENALTIES]: {
        penalties_penalties: null,
        penalties_yards: null,
    },
    // NFL
    [STATSGROUP.PUNT_RETURNS]: {
        punt_returns_avg_yards: null,
        punt_returns_faircatches: null,
        punt_returns_longest: null,
        punt_returns_longest_touchdown: null,
        punt_returns_returns: null,
        punt_returns_touchdowns: null,
        punt_returns_yards: null,
    },
    // NFL
    [STATSGROUP.PUNTS]: {
        punts_attempts: null,
        punts_avg_hang_time: null,
        punts_avg_net_yards: null,
        punts_avg_yards: null,
        punts_blocked: null,
        punts_hang_time: null,
        punts_inside_20: null,
        punts_longest: null,
        punts_net_yards: null,
        punts_return_yards: null,
        punts_touchbacks: null,
        punts_yards: null,
    },
    // NFL
    [STATSGROUP.RECEIVING]: {
        receiving_air_yards: null,
        receiving_avg_yards: null,
        receiving_broken_tackles: null,
        receiving_catchable_passes: null,
        receiving_dropped_passes: null,
        receiving_longest: null,
        receiving_longest_touchdown: null,
        receiving_receptions: null,
        receiving_redzone_targets: null,
        receiving_targets: null,
        receiving_touchdowns: null,
        receiving_yards: null,
        receiving_yards_after_catch: null,
        receiving_yards_after_contact: null,
    },
    // NFL
    [STATSGROUP.RUSHING]: {
        rushing_attempts: null,
        rushing_avg_yards: null,
        rushing_broken_tackles: null,
        rushing_kneel_downs: null,
        rushing_longest: null,
        rushing_longest_touchdown: null,
        rushing_redzone_attempts: null,
        rushing_scrambles: null,
        rushing_tlost: null,
        rushing_tlost_yards: null,
        rushing_touchdowns: null,
        rushing_yards: null,
        rushing_yards_after_contact: null,
    },
    // NFL
    [STATSGROUP.TOUCHDOWNS]: {
        touchdowns_fumble_return: null,
        touchdowns_int_return: null,
        touchdowns_kick_return: null,
        touchdowns_other: null,
        touchdowns_pass: null,
        touchdowns_punt_return: null,
        touchdowns_rush: null,
        touchdowns_total: null,
        touchdowns_total_return: null,
    },


    // NHL
    [STATSGROUP.POWERPLAY]: {
        powerplay_shots: null,
        powerplay_goals: null,
        powerplay_missed_shots: null,
        powerplay_assists: null,
        powerplay_opportunities: null,
        powerplay_percentage: null,
        powerplay_strength_5v4_shots: null,
        powerplay_strength_5v4_goals: null,
        powerplay_strength_5v4_opportunities: null,
        powerplay_strength_5v3_shots: null,
        powerplay_strength_5v3_goals: null,
        powerplay_strength_5v3_opportunities: null,
        powerplay_strength_4v3_shots: null,
        powerplay_strength_4v3_goals: null,
        powerplay_strength_4v3_opportunities: null,
    },

    [STATSGROUP.SHORTHANDED]: {
        shorthanded_shots: null,
        shorthanded_goals: null,
        shorthanded_missed_shots: null,
        shorthanded_assists: null,
        shorthanded_opportunities: null,
        shorthanded_goals_against: null,
        shorthanded_kill_pct: null,
        shorthanded_strength_4v5_shots: null,
        shorthanded_strength_4v5_goals: null,
        shorthanded_strength_3v5_shots: null,
        shorthanded_strength_3v5_goals: null,
        shorthanded_strength_3v4_shots: null,
        shorthanded_strength_3v4_goals: null,
    },
    // NHL
    [STATSGROUP.EVENSTRENGTH]: {
        evenstrength_shots: null,
        evenstrength_goals: null,
        evenstrength_missed_shots: null,
        evenstrength_assists: null,
        evenstrength_strength_5v5_shots: null,
        evenstrength_strength_5v5_goals: null,
        evenstrength_strength_4v4_shots: null,
        evenstrength_strength_4v4_goals: null,
        evenstrength_strength_3v3_shots: null,
        evenstrength_strength_3v3_goals: null,
    },
    // NHL
    [STATSGROUP.PENALTY]: {
        penalty_shots: null,
        penalty_goals: null,
        penalty_missed_shots: null,
    },
    // NHL
    [STATSGROUP.AVERAGE]: {
        average_assists: null,
        average_blocked_shots: null,
        average_points: null,
        average_blocked_att: null,
        average_team_penalty_minutes: null,
        average_penalties: null,
        average_missed_shots: null,
        average_hits: null,
        average_shots: null,
        average_takeaways: null,
        average_giveaways: null,
        average_goals: null,
        average_team_penalties: null,
        average_penalty_minutes: null,
    },
};
